import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import SplashScreen from './components/SplashScreen';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import PermissionRoute from './components/PermissionRoute';
import AiChatRoute from './components/AiChatRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import DlpReportPage from './pages/DlpReportPage';
import NacReportPage from './pages/NacReportPage';
import WebFilterPage from './pages/WebFilterPage';
import EdrThreatPage from './pages/EdrThreatPage';
import EdrReportPage from './pages/EdrReportPage';
import DailyReportPage from './pages/DailyReportPage';
import AdminPage from './pages/AdminPage';
import AiChatLandingPage from './pages/AiChatLandingPage';
import AiChatAdminPage from './pages/AiChatAdminPage';
import Layout from './components/Layout';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import './App.css';

function NotFound() {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '16px' }}>
      <h1 style={{ fontSize: '48px', margin: 0 }}>404</h1>
      <p style={{ fontSize: '18px', color: '#666' }}>페이지를 찾을 수 없습니다.</p>
      <button onClick={() => navigate('/')} style={{ padding: '8px 20px', cursor: 'pointer' }}>홈으로 돌아가기</button>
    </div>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  // PDF 모드(?pdf=true)에서는 Puppeteer가 신속하게 진입해야 하므로 SplashScreen 건너뜀
  const isPdfMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('pdf') === 'true';
  const [serverReady, setServerReady] = useState(isPdfMode);
  const handleReady = useCallback(() => setServerReady(true), []);

  if (!serverReady) {
    return <SplashScreen onReady={handleReady} />;
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <SettingsProvider>
        <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard/apt" replace />} />
              <Route path="dashboard" element={<Navigate to="/dashboard/apt" replace />} />
              <Route
                path="dashboard/apt"
                element={
                  <PermissionRoute pageType="APT">
                    <Dashboard />
                  </PermissionRoute>
                }
              />
              <Route
                path="dashboard/dlp"
                element={
                  <PermissionRoute pageType="DLP">
                    <DlpReportPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="dashboard/nac"
                element={
                  <PermissionRoute pageType="NAC">
                    <NacReportPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="dashboard/web-filter"
                element={
                  <PermissionRoute pageType="WEB_FILTER">
                    <WebFilterPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="dashboard/edr"
                element={
                  <PermissionRoute pageType="EDR_REPORT">
                    <EdrReportPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="dashboard/edr-threat"
                element={
                  <PermissionRoute pageType="EDR_THREAT">
                    <EdrThreatPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="dashboard/daily-report"
                element={
                  <PermissionRoute pageType="DAILY_REPORT">
                    <DailyReportPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="admin"
                element={
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                }
              />
              <Route
                path="ai-chat"
                element={
                  <AiChatRoute>
                    <AiChatLandingPage />
                  </AiChatRoute>
                }
              />
              <Route
                path="admin/ai-chat"
                element={
                  <AdminRoute>
                    <AiChatAdminPage />
                  </AdminRoute>
                }
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
        </SettingsProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
